import React from 'react';
import PropTypes from 'prop-types';

import { ContentZonesService, LocaleService, WindowProxy } from '@sm360/widget-commons-core-services';
import {
    CONSTANT as GENERALCONSTANT,
    Account,
    AnalyticsService,
    MEDIA_QUERIES,
    AdobeService,
    ADOBE_TAGS,
    DATA_LAYER_VARIABLES as DATA_LAYER_VARS,
    AccountService,
    VIRTUAL_PAGEVIEWS as PAGES,
    ShiftDigitalService,
} from '@sm360/widget-commons-general';
import { FIELD_LENGTH, InputCheckbox, InputEmail, InputText, ValidationService } from '@sm360/widget-commons-forms';
import { Quote } from '@sm360/widget-commons-vehicle';

import LoadingButton from 'login/components/Library/LoadingButton/LoadingButton.jsx';
import PasswordGroup from 'login/components/Library/PasswordGroup/PasswordGroup.jsx';

import AuthenticationService from 'login/services/AuthenticationService/AuthenticationService.js';
import ConfigsService from 'login/services/ConfigsService/ConfigsService.js';

import CONSTANT from 'login/constants/constant.js';
import TAGS from 'login/constants/trackingTags';

const czGroupName = 'widget.login.forms.signup';

class SignUpForm extends React.Component {
    constructor(props) {
        super(props);

        this.handleFormSubmission = this.handleFormSubmission.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleInputChange = this.handleInputChange.bind(this);
        this.passwordChangeCallback = this.passwordChangeCallback.bind(this);
        this.handleCheckboxChange = this.handleCheckboxChange.bind(this);
        this.sendForm = this.sendForm.bind(this);
        this.createAccount = this.createAccount.bind(this);

        this.state = {
            formInputs: {
                fullname:
                    props.account.firstname && props.account.lastname
                        ? `${props.account.firstname} ${props.account.lastname}`
                        : '',
                email: props.account.email,
                password: '',
                confirmPassword: '',
                phoneNumber: props.account.phoneNumber,
                receivePromotionalEmail: false,
            },
            formValidation: {
                email: ValidationService.validateEmail(props.account.email),
                password: false,
                fullname:
                    (ValidationService.validateIsNotEmpty(props.account.firstname) &&
                        ValidationService.validateIsNotEmpty(props.account.lastname)) ||
                    false,
            },
            errorMessage: '',
            submitButtonEnabled: true,
        };

        this.isFieldRequired = true;

        this.promotionText = ContentZonesService.getContentZone(
            czGroupName,
            'widget.login.forms.signup.promotiontext'
        ).getValue();
        this.buttonText = ContentZonesService.getContentZone(
            czGroupName,
            'widget.login.forms.signup.buttontext'
        ).getValue();
    }

    onFocus(name) {
        const windowGet = WindowProxy.get();
        const windowSize = window.innerWidth;
        const isMobileSize = windowSize <= MEDIA_QUERIES.MOBILE.MAX;
        const focusedElement = windowGet.document.getElementById(name);

        if (isMobileSize && focusedElement) {
            focusedElement.scrollIntoView();
        }
    }

    getRegisterErrorMessageCode(response) {
        let errorCode = CONSTANT.REGISTRATION_ERROR_KEYS.AUTHENTICATION_ERROR;

        if (response && response.status === 409) {
            errorCode = CONSTANT.REGISTRATION_ERROR_KEYS.EMAIL_ALREADY_REGISTERED;
        }

        return LocaleService.t(errorCode);
    }

    handleCheckboxChange(value) {
        this.setState((prevState) => ({
            formInputs: {
                ...prevState.formInputs,
                receivePromotionalEmail: !value,
            },
        }));
    }

    handleInputChange(value, name) {
        let validation;
        if (name === 'email') {
            validation = ValidationService.validateEmail(value);
        } else if (name === 'fullname') {
            const nameArray = value.split(' ').filter(Boolean);

            validation = ValidationService.validateIsNotEmpty(value) && nameArray.length >= 2;
        } else {
            validation = ValidationService.validateIsNotEmpty(value);
        }

        this.setState((prevState) => ({
            formInputs: {
                ...prevState.formInputs,
                [name]: value,
            },
            formValidation: {
                ...prevState.formValidation,
                [name]: validation,
            },
        }));
    }

    passwordChangeCallback(groupResponse) {
        const { valid, value } = groupResponse;

        this.setState((prevState) => ({
            formInputs: {
                ...prevState.formInputs,
                password: value,
                confirmPassword: value,
            },
            formValidation: {
                ...prevState.formValidation,
                password: valid,
            },
        }));
    }

    handleSubmit(event) {
        event.preventDefault();
    }

    handleFormSubmission() {
        if (!ValidationService.validateForm(this.state.formValidation)) {
            return;
        }

        this.sendForm();
    }

    sendForm() {
        this.setState({
            errorMessage: '',
            submitButtonEnabled: false,
        });
        this.createAccount(this.props.quote, this.props.submitLead);
    }

    createAccount(quote, submitLead) {
        AuthenticationService.createAccountAndAuthenticate(this.state.formInputs, quote, submitLead).then(
            (newAccount) => {
                AnalyticsService.setDataLayerVariable(DATA_LAYER_VARS.IS_LOGGEDIN, true);
                AccountService.getLoggedAccount().then((account) => {
                    if (account) {
                        AnalyticsService.setDataLayerVariable(DATA_LAYER_VARS.USER_ID, account?.id);
                        AnalyticsService.virtualPageView(PAGES.SIGN_IN(account?.id));
                    }
                });
                AnalyticsService.gaTrigger(TAGS.LOGIN_WIDGET__REGISTER_SUCCESS());
                if (this.props.hasAdobeTagging) {
                    AdobeService.eventTrigger(
                        ADOBE_TAGS.CREATE_AN_ACCOUNT_CTA(GENERALCONSTANT.ACCOUNT.CREATE),
                        'global-onclick'
                    );
                }
                ShiftDigitalService.createFormEvent(newAccount?.contactId, 'account', false, 'email', true);

                setTimeout(() => {
                    this.props.closeDialog({ newAccount });
                }, 500);
            },
            (error) => {
                this.setState({
                    errorMessage: this.getRegisterErrorMessageCode(error.response),
                    submitButtonEnabled: true,
                });
            }
        );
    }

    render() {
        const formClasses = ['form-login'];

        if (!ConfigsService.getFacebookAppId()) {
            formClasses.push('has-not-facebook-login');
        }

        return (
            <div className={formClasses.join(' ')}>
                <form
                    id="login-session-form"
                    name="registerForm"
                    className="registerForm clearfix"
                    onSubmit={this.handleSubmit}
                >
                    <InputText
                        type="text"
                        name="fullname"
                        classNames={['styled-placeholder']}
                        value={this.state.formInputs.fullname}
                        label={LocaleService.t('LOGIN.FULL_NAME')}
                        isRequired={this.isFieldRequired}
                        isValid={this.state.formValidation.fullname}
                        valueChange={this.handleInputChange}
                        // onFocus={this.onFocus}
                        dataTest="login.sign-up.fullname"
                        maxLength={FIELD_LENGTH.FULL_NAME_MAX}
                    />

                    <InputEmail
                        classNames={['styled-placeholder']}
                        label={LocaleService.t('LOGIN.EMAIL')}
                        value={this.state.formInputs.email}
                        isRequired={this.isFieldRequired}
                        isValid={this.state.formValidation.email}
                        valueChange={this.handleInputChange}
                        // onFocus={this.onFocus}
                        dataTest="login.sign-up.email"
                    />
                    <PasswordGroup
                        value={this.state.formInputs.password}
                        // onFocus={this.onFocus}
                        passwordLabel={LocaleService.t('LOGIN.PASSWORD')}
                        confirmPasswordLabel={LocaleService.t('LOGIN.PASSWORD_CONFIRMATION')}
                        valueChange={this.passwordChangeCallback}
                        dataTestPrefix="login.sign-up"
                    />

                    <InputCheckbox
                        id="receivePromotionalEmail"
                        classNames={['full', 'styled-placeholder checkbox-input']}
                        value={this.state.formInputs.receivePromotionalEmail}
                        label={this.promotionText}
                        isValid={this.state.formValidation.receivePromotionalEmail}
                        valueChange={this.handleCheckboxChange}
                    />

                    <div className="error-message">{this.state.errorMessage}</div>

                    <LoadingButton
                        text={this.buttonText}
                        btnEnabled={this.state.submitButtonEnabled}
                        btnCallback={() => this.handleFormSubmission()}
                        dataTest="login.sign-up.submit"
                    />
                </form>
            </div>
        );
    }
}

SignUpForm.defaultProps = {
    quote: new Quote.QuoteModel(),
    submitLead: true,
    hasAdobeTagging: false,
};

SignUpForm.propTypes = {
    closeDialog: PropTypes.func.isRequired,
    account: PropTypes.instanceOf(Account).isRequired,
    quote: PropTypes.instanceOf(Quote.QuoteModel),
    submitLead: PropTypes.bool,
    hasAdobeTagging: PropTypes.bool,
};

SignUpForm.displayName = 'SignUpForm';

export default SignUpForm;
